// InviteDriverScreen — Phase 2 Plan 02-05 Task 2 (DRV-01); migrated to
// telegram-ui in Phase 12 Plan 12-08 (UIR-07, UI-SPEC §7).
//
// Four-state UI (state machine: form → generating → showing_qr → registered):
//   1. form        — input для optional driver_name_hint + Generate Button.
//   2. generating  — Spinner пока POST /api/v1/driver-invites выполняется.
//   3. showing_qr  — QR PNG + short_code XXXX-XXXX-XXXX-XXXX + deep_link
//                    + iOS-warning. react-query polls status каждые 5 сек.
//   4. registered  — после status='used' polling переключает на success view.
//
// Источники истины:
//   - 02-CONTEXT.md D-01..D-06 (token, deep link, short-code, mini app surface)
//   - 12-UI-SPEC §7 (telegram-ui shell: Section/Cell + QR + Button)

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Cell, List, Placeholder, Section, Spinner } from "@telegram-apps/telegram-ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  type DriverInviteResponse,
  createDriverInvite,
  getInviteStatus,
} from "../api/driverInvites";
import { useAuth } from "../auth/useAuth";

const formSchema = z.object({
  driver_name_hint: z.string().max(100, "Name too long (max 100)").optional().or(z.literal("")),
});
type FormData = z.infer<typeof formSchema>;
type Screen = "form" | "generating" | "showing_qr" | "registered";

export function InviteDriverScreen(): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient } = useAuth();
  const [screen, setScreen] = useState<Screen>("form");
  const [invite, setInvite] = useState<DriverInviteResponse | null>(null);

  const { register, handleSubmit, formState } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      createDriverInvite(apiClient, {
        driver_name_hint: data.driver_name_hint?.trim() || null,
      }),
    onMutate: () => setScreen("generating"),
    onSuccess: (res) => {
      setInvite(res);
      setScreen("showing_qr");
    },
    onError: () => setScreen("form"),
  });

  // Poll status every 5s when invite is being displayed.
  useQuery({
    queryKey: ["driver-invite-status", invite?.invite_id],
    queryFn: async () => {
      if (invite === null) return null;
      const data = await getInviteStatus(apiClient, invite.invite_id);
      if (data.status === "used") {
        setScreen("registered");
      }
      return data;
    },
    enabled: screen === "showing_qr" && invite !== null,
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    retry: 1,
    staleTime: 0,
  });

  if (screen === "form") {
    return (
      <List>
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} data-testid="invite-form">
          <Section header={t("invite.title", "Invite driver") as string}>
            <div style={{ padding: 16 }}>
              <label htmlFor="driver_name_hint" style={{ display: "block", marginBottom: 8 }}>
                {t("invite.name_hint_label", "Driver name (optional)")}
              </label>
              <input
                id="driver_name_hint"
                type="text"
                maxLength={100}
                placeholder={t("invite.name_hint_placeholder", "e.g. Ivan") as string}
                data-testid="invite-name-input"
                {...register("driver_name_hint")}
                style={{
                  display: "block",
                  width: "100%",
                  padding: 10,
                  border: "1px solid var(--tgui--hint_color, #ccc)",
                  borderRadius: 8,
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ padding: "0 16px 16px" }}>
              <Button
                size="l"
                stretched
                type="submit"
                disabled={!formState.isValid || mutation.isPending}
                loading={mutation.isPending}
                data-testid="invite-generate-button"
              >
                {t("invite.generate", "Generate")}
              </Button>
            </div>
          </Section>
        </form>
      </List>
    );
  }

  if (screen === "generating") {
    return (
      <List>
        <div
          data-testid="invite-generating"
          style={{ display: "flex", justifyContent: "center", padding: 32 }}
        >
          <Spinner size="m" />
        </div>
      </List>
    );
  }

  if (screen === "showing_qr" && invite !== null) {
    return (
      <List data-testid="invite-qr-view">
        <Section header={t("invite.scan_with_phone", "Have the driver scan this QR") as string}>
          <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
            <img
              src={`data:image/png;base64,${invite.qr_png_base64}`}
              alt="Driver invite QR code"
              width={300}
              height={300}
              data-testid="invite-qr-image"
              style={{ display: "block", maxWidth: "100%", height: "auto" }}
            />
          </div>
          <Cell subhead={t("invite.short_code_label", "Or dictate this code") as string} multiline>
            <code data-testid="invite-short-code">{invite.short_code}</code>
          </Cell>
          <Cell subhead={t("invite.deep_link_label", "Or share this link") as string} multiline>
            <code data-testid="invite-deep-link">{invite.deep_link}</code>
          </Cell>
          <Cell data-testid="invite-ios-warning" multiline>
            {t(
              "invite.ios_warning",
              "Tell the driver: do not lock the phone while driving — iOS pauses GPS after 15-20 minutes in background.",
            )}
          </Cell>
          <Cell data-testid="invite-expires-at" multiline>
            {t("invite.expires_at_label", "Expires")}:{" "}
            {new Date(invite.expires_at).toLocaleString()}
          </Cell>
        </Section>
      </List>
    );
  }

  // screen === "registered"
  return (
    <List data-testid="invite-registered">
      <Placeholder
        header={`✓ ${t("invite.registered_title", "Driver registered")}`}
        description={t("invite.registered_hint", "The invite was redeemed successfully.") as string}
      />
    </List>
  );
}
